import React, { useContext, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import * as actionType from "../context/actions";
import PaginationView from "./PaginationView";
import { FilterdData } from "../helper/FilterdData";
import DataNotFoundView from "./DataNotFoundView";
import { useNavigate } from "react-router-dom";
import DeleteExpenseView from "./DeleteExpenseView";
import DeleteSaleReturnView from "./DeleteSaleReturnView";
import { Languages } from "../Utils/Languages";
import { Tooltip } from 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { resolveAuditName } from "../Utils/audit_helper";

const SaleReturnTableDataLayer = ({ SaleReturnData, selectedBusiness }) => {
    const navigate = useNavigate();
    const { state, dispatch } = useContext(ConfigContext);
    const [filterdInvoiceData, setFilterdInvoiceData] = useState(SaleReturnData);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    useEffect(() => {
        setFilterdInvoiceData(FilterdData(state, SaleReturnData, "creationDate"));
    }, [state])

    const enrichInvoiceData = (invoices) => {
        return invoices.map((invoice, index) => {
            // Find customer name
            const matchedCustomer = state?.CUSTOMERS?.find(
                (cust) => cust.customerId?.toString() === invoice.customerId?.toString()
            );
            const customerName = matchedCustomer?.customer || "Unknown";

            // Format issued date safely
            let issuedDate = "";
            try {
                const rawDate = invoice.date;
                if (rawDate) {
                    let ts = parseInt(rawDate);
                    if (!isNaN(ts)) {
                        if (ts.toString().length <= 10) ts *= 1000;
                        const d = new Date(ts);
                        if (!isNaN(d.getTime())) {
                            issuedDate = d.toDateString();
                        }
                    }
                }
            } catch (e) {
                issuedDate = "";
            }

            // Format status
            const paidStatus = (invoice.paidFlagKey === "true" || invoice.paidFlagKey === true)
                ? Languages[state?.LANGUAGE]?.paid || "Paid"
                : Languages[state?.LANGUAGE]?.unpaid || "Unpaid";

            // Calculate due status
            let dueStatus = "No Due Date";
            if (invoice.dueDate) {
                let dueDateNum = parseInt(invoice.dueDate);
                if (dueDateNum.toString().length === 10) {
                    dueDateNum = dueDateNum * 1000;
                }
                const dueDate = new Date(dueDateNum);
                const today = new Date();
                dueDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                const diffTime = dueDate.getTime() - today.getTime();
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays === 0) {
                    dueStatus = "Due Today";
                } else if (diffDays < 0) {
                    dueStatus = `Overdue ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? "day" : "days"}`;
                } else {
                    dueStatus = `Due in ${diffDays} ${diffDays === 1 ? "day" : "days"}`;
                }
            }

            return {
                ...invoice,
                srNo: index + 1,
                customerName, // Add searchable customer name
                issuedDateFormatted: issuedDate, // Add searchable formatted date
                paidStatusText: paidStatus, // Add searchable status text
                dueStatusText: dueStatus // Add searchable due status text
            };
        });
    };

    // Apply enrichment to the matched invoices
    const matchedInvoices = useMemo(() => {
        const filteredData = (selectedBusiness === null || selectedBusiness === "null")
            ? filterdInvoiceData
            : filterdInvoiceData.filter(salereturn => salereturn?.companyId == selectedBusiness);

        return enrichInvoiceData(filteredData);
    }, [filterdInvoiceData, selectedBusiness, state?.CUSTOMERS, state?.LANGUAGE]);

    useEffect(() => {
        // Select all elements with data-bs-toggle="DefaultTooltipThree"
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="DefaultTooltipThree"]');
        // Initialize tooltips
        const tooltipList = Array.from(tooltipTriggerList).map(
            (tooltipTriggerEl) => new Tooltip(tooltipTriggerEl)
        );
        // Cleanup on unmount
        return () => {
            tooltipList.forEach((tooltip) => tooltip.dispose());
        };
    }, []);

    // Pagination logic
    const totalPages = Math.ceil(matchedInvoices?.length / itemsPerPage);

    const sortCaret = (order, column) => {
        return (
            <span className="d-inline-flex align-items-center ms-2" >
                {!order ? (
                    <Icon icon="mdi:swap-vertical" className="text-muted" />
                ) : order === "asc" ? (
                    <Icon icon="mdi:arrow-up-thick" className="text-muted" />
                ) : (
                    <Icon icon="mdi:arrow-down-thick" className="text-muted" />
                )}
            </span>
        );
    };

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const { SearchBar } = Search;

    const columns = [
        {
            dataField: "srNo",
            text: Languages[state?.LANGUAGE]?.s_r,
            headerAlign: "center",
            align: "center",
            sort: true,
            formatter: (cell) => (
                <span style={{ marginLeft: -8 }}>{cell}</span>
            ),
            sortCaret: sortCaret,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row?.invoiceId, row?.paidFlagKey);
                }
            }
        },
        {
            dataField: "id",
            text: Languages[state?.LANGUAGE]?.invoice_no,
            headerAlign: "center",
            align: "center",
            sort: true,
            formatter: (cell, row) => {
                const displayId = row?.customInvoiceId || row?.id;
                const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(row.companyId));
                const isShared = business?.isShared === true;

                const currentUserStr = state?.SET_USER?.name || state?.SET_USER?.email || state?.SET_USER?.contact || "guest";
                const resolvedName = resolveAuditName(row.createdBy, state.membersData);
                const creatorName = (resolvedName === "Unknown" && business?.ownerName)
                    ? business.ownerName
                    : resolvedName;
                const isCreator = creatorName === currentUserStr || row.createdBy === currentUserStr || (typeof row.createdBy === 'object' && row.createdBy?.uid === state?.USERID);

                const showByLabel = row.createdBy
                    ? !isCreator
                    : (isShared && business?.ownerName);

                const displayName = row.createdBy ? creatorName : business?.ownerName;

                return (
                    <div style={{ marginLeft: -8 }}>
                        <div>{displayId}</div>
                        {showByLabel && (
                            <small className="text-secondary-light" style={{ fontSize: '11px', display: 'block', marginTop: '-4px' }}>
                                by: {displayName}
                            </small>
                        )}
                    </div>
                );
            },
            sortCaret: sortCaret,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row?.invoiceId, row?.paidFlagKey);
                }
            }
        },
        {
            dataField: "customerName", // Changed from "customerId" to "customerName"
            text: Languages[state?.LANGUAGE]?.customer_name,
            sort: true,
            style: { minWidth: "150px" },
            sortCaret: sortCaret,
            formatter: (cell, row) => {
                const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(row.companyId));
                const isShared = business?.isShared === true;

                const currentUserStr = state?.SET_USER?.name || state?.SET_USER?.email || state?.SET_USER?.contact || "guest";
                const resolvedName = resolveAuditName(row.createdBy, state.membersData);
                const creatorName = (resolvedName === "Unknown" && business?.ownerName)
                    ? business.ownerName
                    : resolvedName;
                const isCreator = creatorName === currentUserStr || row.createdBy === currentUserStr || (typeof row.createdBy === 'object' && row.createdBy?.uid === state?.USERID);

                const showByLabel = row.createdBy
                    ? !isCreator
                    : (isShared && business?.ownerName);

                const displayName = row.createdBy ? creatorName : business?.ownerName;

                return (
                    <div style={{ marginLeft: -8, cursor: "pointer" }}>
                        <div>{cell || "Unknown"}</div>
                        {/* {showByLabel && (
                            <small className="text-secondary-light" style={{ fontSize: '11px', display: 'block', marginTop: '-4px' }}>
                                by: {displayName}
                            </small>
                        )} */}
                    </div>
                );
            },
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row?.invoiceId, row?.paidFlagKey);
                }
            }
        },
        {
            dataField: "totalAmount",
            text: Languages[state?.LANGUAGE]?.amount,
            sort: true,
            sortCaret: sortCaret,
            formatter: (cell) => (
                <span>{state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL} ` : `${state?.SYMBOL} `} {cell}</span>
            ),
            sortFunc: (a, b, order) => {
                if (order === 'asc') {
                    return Number(a) - Number(b);
                }
                return Number(b) - Number(a);
            },
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row?.invoiceId, row?.paidFlagKey);
                }
            }
        },
        {
            dataField: "issuedDateFormatted", // Changed from "date" to use formatted date
            text: Languages[state?.LANGUAGE]?.issued_date,
            formatter: (cell) => cell, // Just return the already formatted date
            sort: true,
            style: { minWidth: "100px" },
            sortCaret: sortCaret,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row?.invoiceId, row?.paidFlagKey);
                }
            }
        },

        {
            dataField: "paidStatusText", // <-- use the enriched text field
            text: Languages[state?.LANGUAGE]?.status,
            headerAlign: "center",
            align: "center",
            formatter: (cell, row) => (
                <span
                    className={`d-inline-block text-center px-24 py-4 rounded-pill fw-medium text-sm ${(row?.paidFlagKey === "true" || row?.paidFlagKey === true)
                        ? "bg-success-focus text-success-main"
                        : "bg-danger-focus text-danger-main"}`}
                >
                    {cell}
                </span>
            ),
            sort: true,
            sortCaret: sortCaret,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row?.invoiceId, row?.paidFlagKey);
                }
            }
        }
        ,
        {
            dataField: "action",
            text: Languages[state?.LANGUAGE]?.action,
            headerAlign: "center",
            align: "center",
            formatter: (cell, row) => (
                console.log(row),
                <>
                    <Link
                        onClick={(e) => {
                            if (!state?.USERID) {
                                e.preventDefault();
                                dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                return;
                            }
                            dispatch({ type: actionType.SELECTEDINVOICE, payload: row?.invoiceId });
                        }}
                        to="/invoice-preview"
                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-primary"
                        data-bs-title="View Sales Return"
                    >
                        <Icon icon="iconamoon:eye-light" />
                    </Link>
                    <Link
                        onClick={(e) => {
                            if (!state?.USERID) {
                                e.preventDefault();
                                dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                return;
                            }
                            dispatch({ type: actionType.SELECTEDINVOICE, payload: row?.invoiceId });
                        }}
                        to="/sales-return-edit"
                        className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-success"
                        data-bs-title="Edit Sales Return"
                    >
                        <Icon icon="lucide:edit" />
                    </Link>
                    <Link
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-danger"
                        data-bs-title="Delete Sales Return"
                    >
                        <DeleteSaleReturnView FirebaseId={row?.uniqueID} />
                    </Link>
                </>
            ),
        }
    ];

    const OnClickOfRow = (invoiceId, isPaid) => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }
        dispatch({ type: actionType.SELECTEDINVOICE, payload: invoiceId })
        if (isPaid == "true" || isPaid == true) {
            navigate("/invoice-preview")
        }
        else {
            navigate("/sales-return-edit")
        }
    }

    const handleSelection = (val) => {
        setItemsPerPage(val)
        setCurrentPage(1)
    };

    return (
        <div className="card">
            <div className="card-body">
                <ToolkitProvider keyField="invoiceId" data={matchedInvoices} columns={columns} search>
                    {props => {
                        const searchText = props.searchProps.searchText?.toLowerCase() || "";
                        const filtered = searchText
                            ? matchedInvoices.filter(item =>
                                item.customerName?.toLowerCase()?.includes(searchText) ||
                                item.id?.toString()?.toLowerCase().includes(searchText) ||
                                item.issuedDateFormatted?.toLowerCase()?.includes(searchText) ||
                                item.totalAmount?.toString()?.toLowerCase().includes(searchText) ||
                                item.customInvoiceId?.toString()?.toLowerCase().includes(searchText)
                            )
                            : matchedInvoices;

                        const start = (currentPage - 1) * itemsPerPage;
                        const paginatedData = filtered.slice(start, start + itemsPerPage);
                        const adjustedTotalPages = Math.ceil(filtered.length / itemsPerPage);

                        return (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <SearchBar
                                        {...props.searchProps}
                                        placeholder={Languages[state?.LANGUAGE]?.SaleReturn_Search}
                                        className="form-control w-auto"
                                    />
                                </div>

                                <div className="table-responsive">
                                    <BootstrapTable
                                        {...props.baseProps}
                                        data={paginatedData}
                                        bootstrap4
                                        bordered={false}
                                        wrapperClasses={`custom-table-border invoice-table ${localStorage.getItem("theme") === "dark" ? "table-dark" : "table-light"
                                            }`}
                                        defaultSorted={{ dataField: "date", order: "desc" }}
                                        noDataIndication={() => <DataNotFoundView height={400} />}
                                        rowStyle={{ cursor: "pointer" }}
                                    />
                                </div>

                                {paginatedData.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="d-flex align-items-center gap-2 entry_per_page">
                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon"
                                                    type="button"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    {itemsPerPage}
                                                </button>
                                                <ul className="dropdown-menu shadow-md">
                                                    {[5, 10, 15, 20].map(value => (
                                                        <li key={value}>
                                                            <Link
                                                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${itemsPerPage == value && "bg-primary-50"
                                                                    }`}
                                                                onClick={() => handleSelection(value)}
                                                            >
                                                                {value}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <span className="text-secondary-light">
                                                {Languages[state?.LANGUAGE]?.entries_per_page}
                                            </span>
                                        </div>

                                        <div className="ms-auto">
                                            <PaginationView
                                                currentPage={currentPage}
                                                totalPages={adjustedTotalPages}
                                                handlePageChange={setCurrentPage}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    }}
                </ToolkitProvider>
            </div>
        </div>

    );
};

export default SaleReturnTableDataLayer;